import { useProjectSettings } from "@/hooks/useProjectSettings"
import { useNetwork } from "@/hooks/useNetwork"
import { isDBCAPointSourceKind } from "@fieldbook/common/types"
import {
  DbcaFaunaDetails,
  DbcaFaunaMapLayer,
  DBCA_FAUNA_LAYER_ID,
} from "@fieldbook/common/components"
import { isFaunaProperties } from "@fieldbook/common/utils"
import { FieldbookMap } from "@/components/common/FieldbookMap"
import {
  useContextSources,
  useContextPointRead,
  useDocumentOccurrences,
} from "@/hooks/useReferenceData"
import { isStrictPosition, type DBCAFeature } from "@fieldbook/common/types"
import { TaxonName } from "@fieldbook/common/components"
import { Badge } from "@fieldbook/ui/badge"
import { createFileRoute } from "@tanstack/react-router"
import { ArrowLeft, Navigation } from "lucide-react"
import { useMemo, useState, useEffect } from "react"
import { Layer, Source, useMap, Popup } from "react-map-gl/mapbox"
import type { ExpressionSpecification, MapMouseEvent, LngLat } from "mapbox-gl"
import { featureCollection } from "@turf/turf"
import { useActiveNavTarget } from "@/hooks/useActiveNavTarget"
import { Button } from "@fieldbook/ui/button"
import {
  borangaDetailFields,
  conservationColorExpression,
  documentOccurrenceToFeature,
  formatConservationCode,
  formatSourceFormat,
  getConservationHexColor,
} from "@fieldbook/common/utils"

export const Route = createFileRoute(
  "/_private/projects/$id/data/context/species/$taxon",
)({
  component: ReferenceDataSpeciesMap,
})

type PopupDetails = {
  feature: DBCAFeature
  coords: LngLat
}

// Per-source record identifier, used to key the navigation target.
function occurrenceKey(props: DBCAFeature["properties"]) {
  switch (props.sourceFormat) {
    case "tpfa":
      return props.sourceId
    case "bc_roost":
      return props.siteCode
    case "bc_breeding":
      return props.siteId
    case "tpfl":
      return props.popNumber
    case "waherb":
      return props.sheet
    case "boranga":
      return props.occurrenceNumber
  }
}

function ReferenceDataSpeciesMap() {
  const { id: projectId, taxon } = Route.useParams()
  const navigate = Route.useNavigate()
  const { data: sources = [] } = useContextSources(projectId)
  const { settings } = useProjectSettings(projectId)
  const { isOnline } = useNetwork()
  const pointSourceIds = useMemo(
    () =>
      sources
        .filter((source) => isDBCAPointSourceKind(source.kind))
        .map((source) => source.id),
    [sources],
  )
  const { tpfl, waherb, boranga, tpfa, bcRoost, bcBreeding } =
    useContextPointRead(
      projectId,
      pointSourceIds,
      settings.syncedContextSourceIds,
      isOnline,
    )
  const { data: documentOccurrences } = useDocumentOccurrences(projectId)

  // Filter features for this species, from both DBCA context sources and the
  // occurrences extracted from linked documents (matching how the count on the
  // species page is derived).
  const speciesFeatures = useMemo(() => {
    const features: DBCAFeature[] = []
    const collections = [tpfl, waherb, boranga, tpfa, bcRoost, bcBreeding]

    collections.forEach((rd) => {
      rd.features.forEach((feature) => {
        const props = feature.properties
        if (props.taxon === taxon) {
          features.push(feature as DBCAFeature)
        }
      })
    })

    documentOccurrences.forEach((occ) => {
      if ((occ.species?.name ?? occ.taxon) === taxon) {
        const feature = documentOccurrenceToFeature(occ)
        if (feature) features.push(feature)
      }
    })

    return features
  }, [
    tpfl,
    waherb,
    boranga,
    tpfa,
    bcRoost,
    bcBreeding,
    documentOccurrences,
    taxon,
  ])

  const geoJSON = useMemo(
    () => featureCollection(speciesFeatures as GeoJSON.Feature[]),
    [speciesFeatures],
  )

  const firstFeature = speciesFeatures[0]
  const conservationCode = firstFeature
    ? firstFeature.properties.conservationCode
    : undefined

  return (
    <div className="bg-background fixed inset-0 z-50 flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b p-3">
        <button
          onClick={() =>
            firstFeature?.properties.speciesId
              ? navigate({
                  to: "/projects/$id/data/species/$speciesId",
                  params: {
                    id: projectId,
                    speciesId: firstFeature?.properties.speciesId,
                  },
                })
              : navigate({
                  to: "/projects/$id/data/context",
                  params: {
                    id: projectId,
                  },
                })
          }
          className="text-muted-foreground flex items-center gap-2"
        >
          <ArrowLeft size={20} />
          <span>Back</span>
        </button>
        <div className="flex flex-col items-end gap-2">
          <TaxonName name={taxon} className="text-lg font-medium" />
          <div className="flex items-center gap-2">
            {conservationCode && (
              <Badge
                style={{
                  backgroundColor: getConservationHexColor(conservationCode),
                  color:
                    conservationCode === "T" || conservationCode === "1"
                      ? "white"
                      : "black",
                }}
                className="text-xs"
              >
                {formatConservationCode(conservationCode)}
              </Badge>
            )}
            <span className="text-muted-foreground text-xs">
              {speciesFeatures.length} location
              {speciesFeatures.length !== 1 ? "s" : ""}
            </span>
          </div>
        </div>
      </div>

      {/* Map */}
      <div className="flex-1">
        {speciesFeatures.length > 0 ? (
          <FieldbookMap geoJSON={geoJSON}>
            <SpeciesReferenceDataLayers features={geoJSON} />
          </FieldbookMap>
        ) : (
          <div className="flex h-full items-center justify-center">
            <p className="text-muted-foreground">No reference data found</p>
          </div>
        )}
      </div>
    </div>
  )
}

function SpeciesReferenceDataLayers({
  features,
}: {
  features: GeoJSON.FeatureCollection
}) {
  const { current: map } = useMap()
  const [popupDetails, setPopupDetails] = useState<PopupDetails | null>(null)

  // Handle click events
  useEffect(() => {
    if (!map) return

    const handleClick = (e: MapMouseEvent) => {
      const clickedFeatures = e.features
      if (!clickedFeatures || clickedFeatures.length === 0) return

      setPopupDetails({
        feature: clickedFeatures[0] as unknown as DBCAFeature,
        coords: e.lngLat,
      })
    }

    map.on("click", DBCA_FAUNA_LAYER_ID, handleClick)
    map.on("click", "species-reference-points", handleClick)
    map.on("mouseenter", "species-reference-points", () => {
      map.getCanvas().style.cursor = "pointer"
    })
    map.on("mouseleave", "species-reference-points", () => {
      map.getCanvas().style.cursor = ""
    })

    return () => {
      map.off("click", DBCA_FAUNA_LAYER_ID, handleClick)
      map.off("click", "species-reference-points", handleClick)
    }
  }, [map])

  return (
    <>
      <DbcaFaunaMapLayer features={features.features as DBCAFeature[]} />
      <Source
        id="species-reference"
        type="geojson"
        data={{
          ...features,
          features: features.features.filter(
            (f) =>
              !isFaunaProperties(f.properties as DBCAFeature["properties"]),
          ),
        }}
      >
        <Layer
          id="species-reference-points"
          type="circle"
          paint={{
            "circle-radius": [
              "interpolate",
              ["linear"],
              ["zoom"],
              8,
              6,
              12,
              10,
              16,
              14,
            ],
            "circle-color":
              conservationColorExpression as ExpressionSpecification,
            "circle-stroke-color": "#FFFFFF",
            "circle-stroke-width": 2,
            "circle-opacity": 0.9,
          }}
        />
        <Layer
          id="species-reference-labels"
          type="symbol"
          layout={{
            "text-field": ["get", "locality"],
            "text-size": 11,
            "text-font": ["Open Sans Bold"],
            "text-offset": [0, 1.8],
            "text-anchor": "top",
            "text-max-width": 12,
          }}
          paint={{
            "text-color": "#000000",
            "text-halo-color": "#FFFFFF",
            "text-halo-width": 2,
            "text-halo-blur": 1,
            "text-opacity": ["interpolate", ["linear"], ["zoom"], 12, 0, 14, 1],
          }}
          minzoom={12}
        />
      </Source>

      {popupDetails && (
        <FeaturePopup
          feature={popupDetails.feature}
          coords={popupDetails.coords}
          onClose={() => setPopupDetails(null)}
        />
      )}
    </>
  )
}

function FeaturePopup({
  feature,
  coords,
  onClose,
}: {
  feature: DBCAFeature
  coords: LngLat
  onClose: () => void
}) {
  const { properties: props, geometry } = feature
  const isTPFL = props?.sourceFormat === "tpfl"
  const isBoranga = props?.sourceFormat === "boranga"
  const navigate = Route.useNavigate()
  const { id } = Route.useParams()

  const [, setNavTarget] = useActiveNavTarget()

  const handleNavigateToItem = () => {
    const pos = geometry.coordinates
    const isStrictCoords = isStrictPosition(pos)
    if (!isStrictCoords) return

    setNavTarget({
      coordinates: pos,
      id: `${props?.speciesId}-${occurrenceKey(props)}`,
      type: "reference_item",
    })
    onClose()
    navigate({
      to: "/projects/$id",
      params: { id },
    })
  }

  return (
    <Popup
      latitude={coords.lat}
      longitude={coords.lng}
      onClose={onClose}
      closeOnClick={false}
      maxWidth="300px"
    >
      <TaxonName name={props.taxon} className="text-sm font-medium" />
      <div className="flex justify-end">
        <Button
          size={"sm"}
          className="items-center space-x-2"
          onClick={handleNavigateToItem}
        >
          <Navigation className="size-4" />
          <span>
            {isFaunaProperties(props)
              ? "Navigate to mapped location"
              : "Navigate"}
          </span>
        </Button>
      </div>
      <div className="max-h-60 overflow-y-auto p-1 text-gray-900">
        <div className="mb-2 flex items-center justify-between">
          <Badge
            variant="outline"
            className="border-gray-300 text-xs text-gray-700"
          >
            {/* Extracted document occurrences carry no sourceFormat */}
            {props.sourceFormat
              ? formatSourceFormat(props.sourceFormat)
              : "Document"}
          </Badge>
        </div>

        <div className="space-y-1 text-xs">
          {isFaunaProperties(props) && <DbcaFaunaDetails properties={props} />}
          {props.locality && (
            <div>
              <span className="text-gray-500">Locality:</span>{" "}
              <span className="font-medium text-gray-900">
                {props.locality}
              </span>
            </div>
          )}

          {isTPFL && (
            <>
              {"popNumber" in props && props.popNumber && (
                <div>
                  <span className="text-gray-500">Population:</span>{" "}
                  <span className="font-medium text-gray-900">
                    {props.popNumber}
                  </span>
                </div>
              )}
              {"countDate" in props && props.countDate && (
                <div>
                  <span className="text-gray-500">Count Date:</span>{" "}
                  <span className="font-medium text-gray-900">
                    {props.countDate}
                  </span>
                </div>
              )}
              {"liveTotal" in props && props.liveTotal !== undefined && (
                <div>
                  <span className="text-gray-500">Live Total:</span>{" "}
                  <span className="font-medium text-gray-900">
                    {props.liveTotal}
                  </span>
                </div>
              )}
            </>
          )}

          {isBoranga &&
            borangaDetailFields(props).map(({ label, value }) => (
              <div key={label}>
                <span className="text-gray-500">{label}:</span>{" "}
                <span className="font-medium text-gray-900">{value}</span>
              </div>
            ))}

          {!isTPFL && !isBoranga && (
            <>
              {"date" in props && props.date && (
                <div>
                  <span className="text-gray-500">Date:</span>{" "}
                  <span className="font-medium text-gray-900">
                    {props.date}
                  </span>
                </div>
              )}
              {"precision" in props && props.precision && (
                <div>
                  <span className="text-gray-500">Geospatial Precision:</span>{" "}
                  <span className="font-medium text-gray-900">
                    {props.precision}
                  </span>
                </div>
              )}
              {"notes" in props && props.notes && (
                <div>
                  <span className="text-gray-500">Notes:</span>{" "}
                  <span className="font-medium text-gray-900">
                    {props.notes}
                  </span>
                </div>
              )}
            </>
          )}

          <div>
            <span className="text-gray-500">Coordinates:</span>{" "}
            <span className="font-medium text-gray-900">
              {coords.lat.toFixed(6)}, {coords.lng.toFixed(6)}
            </span>
          </div>
        </div>
      </div>
    </Popup>
  )
}
